import { useMemo } from 'react'
import { useRestQuery } from '@/lib/hook/tanstackQuery'
import { ApiQueryEndpoint } from '@/src/ApiQueryEndpoint'

interface Return {
  dataCounterProducts: { totalQuantity: number } | undefined
  loadingCounterProducts: boolean
  refetchCounterProducts: () => Promise<void>
}

/**
 * Counter produits panier via REST cart_content_lite (PharmeCartContentLite entity).
 * Backend retourne un nombre brut (total quantity).
 */
const useBasketCounterProducts = ({ isAuthenticated }: { isAuthenticated: boolean }): Return => {
  const { data, isLoading, refetch } = useRestQuery<number | string>({
    ...ApiQueryEndpoint.BASKET.GET_BASKET_TOTAL_QUANTITE,
    queryKey: [ApiQueryEndpoint.BASKET.GET_BASKET_TOTAL_QUANTITE.endpoint],
    enabled: !!isAuthenticated,
    refetchOnWindowFocus: false,
  })

  const refetchCounterProducts = async (): Promise<void> => {
    if (isAuthenticated) {
      await refetch()
    }
  }

  // Type guard : data must be number/string scalar. Object/array -> NaN avoided.
  const isScalar = data != null && (typeof data === 'number' || typeof data === 'string')
  const totalQuantity = isScalar ? Number(data) : 0
  const safeQuantity = Number.isFinite(totalQuantity) ? totalQuantity : 0

  // Stable ref via useMemo : evite cascade re-render sur consumers (useEffect deps).
  const dataCounterProducts = useMemo(
    () => (data != null ? { totalQuantity: safeQuantity } : undefined),
    [data, safeQuantity]
  )

  return useMemo(
    () => ({
      dataCounterProducts,
      loadingCounterProducts: isLoading,
      refetchCounterProducts,
    }),
    [dataCounterProducts, isLoading]
  )
}

export default useBasketCounterProducts
